import { gql, useMutation } from 'urql';

export const SEND_AUTH_QRCODE_TO_ORGANIZER_MUTATION = gql`
  mutation SendAuthQrcodeToOrganizer($users: [Int!]!, $message: String!, $eventId: Int!, $channel: OrganizerChannel) {
    sendAuthQrcodeToOrganizer(info: { users: $users, message: $message, eventId: $eventId, channel: $channel }) {
      totalMessages
      failedUserIds
    }
  }
`;

export enum OrganizerChannel {
  Sms = 'SMS',
  Vk = 'VK',
}

export interface SendAuthQrcodeVariables {
  users: number[];
  message: string;
  eventId: number;
  channel: OrganizerChannel;
}

export interface SendAuthQrcodeResponse {
  sendAuthQrcodeToOrganizer: {
    totalMessages: number;
    failedUserIds: number[];
  };
}

export type SendQrcodeOutcome =
  | { status: 'success' }
  | { status: 'partial'; failedCount: number }
  | { status: 'error' };

export const SEND_QRCODE_TIMEOUT_MS = 60000;

const TIMEOUT_OUTCOME: SendQrcodeOutcome = { status: 'error' };

export const getSendQrcodeToast = (outcome: SendQrcodeOutcome): [string, 'success' | 'error'] => {
  if (outcome.status === 'success') return ['QR-коды отправлены', 'success'];
  if (outcome.status === 'partial') {
    return [`Не удалось отправить QR-коды: ${outcome.failedCount} организаторов`, 'error'];
  }
  return ['Ошибка отправки', 'error'];
};

export const useSendAuthQrcodeToOrganizer = () => {
  const [{ fetching }, executeMutation] = useMutation<SendAuthQrcodeResponse, SendAuthQrcodeVariables>(
    SEND_AUTH_QRCODE_TO_ORGANIZER_MUTATION
  );

  // Отправка синхронная и в канале VK Teams доходит до 36 секунд, поэтому ответ ждём с запасом.
  // Таймаут не отменяет отправку: она завершается на сервере.
  const sendAuthQrcode = async (variables: SendAuthQrcodeVariables): Promise<SendQrcodeOutcome> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const timeout = new Promise<SendQrcodeOutcome>((resolve) => {
      timeoutId = setTimeout(() => resolve(TIMEOUT_OUTCOME), SEND_QRCODE_TIMEOUT_MS);
    });

    const request = executeMutation(variables).then<SendQrcodeOutcome>((result) => {
      const failedUserIds = result.data?.sendAuthQrcodeToOrganizer.failedUserIds;

      if (result.error || !failedUserIds) return { status: 'error' };
      if (failedUserIds.length) return { status: 'partial', failedCount: failedUserIds.length };

      return { status: 'success' };
    });

    try {
      return await Promise.race([request, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return { fetching, sendAuthQrcode };
};
